import { Directive, booleanAttribute, computed, input } from '@angular/core';
import { BAND_TONES, type BandTone } from '@groove/core';

// The tone vocabulary is owned by @groove/core (framework-neutral, so the
// coverage engine can read it without pulling in @angular/core). Re-exported
// here so consumers can keep importing it from the directive.
export { BAND_TONES, type BandTone };

/**
 * Tinted section (component tier). The band PROVIDES the tint + block padding;
 * the CONSUMER composes the inner measure-bounded column (e.g. `.groove-center`)
 * — the band does not cap width itself.
 *
 * Tone → surface-ladder step:
 *   - default → bg-page    (the base field)
 *   - deep    → bg-sunken  (the deep, recessed band)
 *   - raised  → bg-raised  (a lifted band)
 *
 * Container-honest by default: the band is an INSET panel (modest radius) that
 * fills its container — it does NOT break out. True edge-to-edge full-bleed is
 * opt-in via `bleed`, applied ONLY where the layout allows the breakout (a host
 * that escapes the content gutter — e.g. a page with no aside). On an
 * aside-bearing page a bleeding band would only span the content column and
 * read as a misaligned block, so those pages keep the inset default.
 *
 * Padding rides space tokens, so it is density-responsive for free. Per-instance
 * overrides via --_band-* private custom properties.
 */
@Directive({
  selector: '[grooveBand]',
  standalone: true,
  host: {
    class: 'groove-band',
    '[attr.data-tone]': 'toneAttr()',
    '[attr.data-bleed]': 'bleedAttr()',
  },
})
export class GrooveBandDirective {
  readonly tone = input<BandTone>('default');

  // Full-bleed (edge-to-edge, square corners). The consumer's layout must allow
  // the breakout; the modifier supplies the edge-to-edge visual treatment.
  readonly bleed = input(false, { transform: booleanAttribute });

  // Omit the default attr to keep the DOM clean (omit-default-attribute pattern).
  protected readonly toneAttr = computed(() =>
    this.tone() === 'default' ? null : this.tone(),
  );
  protected readonly bleedAttr = computed(() => (this.bleed() ? '' : null));
}
